import React, { ChangeEvent } from 'react';
import { Button, Card, CardContent, FormControl, Grid, Typography } from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { AISummaryDialogProps } from './types';
import { AutocompleteValueType } from '../../pages/document-details/types';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import styles from './AISummaryDialog.module.scss';

const AISummaryDialog = function ({
  onClose,
  handleAnalyzeFunction,
  documentType,
  setDocumentType,
  purpose,
  setPurpose,
  resultFormat,
  setResultFormat,
  detailLevel,
  setDetailLevel,
  simplifyJargon,
  setSimplifyJargon,
  contextualInformation,
  setContextualInformation,
  purposes,
}: AISummaryDialogProps) {
  const { t } = useTranslation();
  const icon = <CheckBoxOutlineBlankIcon fontSize='medium' />;
  const checkedIcon = <CheckBoxIcon fontSize='medium' />;

  return (
    <>
      {/* <Modal open={open} onClose={onClose} closeAfterTransition className='action-modal'>*/}
      <Grid component='div' container>
        <Grid component='div' size={{ xs: 12 }}>
          <Card>
            <CardContent sx={{ padding: 0 }}>
              <Grid
                component='div'
                size={{ xs: 12 }}
                className={styles.title_container}
                style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}
              >
                {/*<AIIcon />
                <Typography variant='h6' gutterBottom>
                  {documentName}
                </Typography>*/}
              </Grid>
              <Grid
                component='div'
                style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}
              >
                <Typography>{t('messages.reviewSettings')}</Typography>
              </Grid>
              <Grid component='div' container spacing={2} style={{ marginTop: '10px' }}>
                <Grid component='div' size={{ xs: 12, md: 12 }}>
                  <FormControl sx={{ width: '100%' }}>
                    <InputLabel htmlFor='documentTypeSelect'>{t('labels.documentType')}</InputLabel>
                    <Select
                      id='aiSummaryDialogDocumentTypeSelect'
                      slotProps={{ input: { id: 'documentTypeSelect' } }}
                      onChange={(e: SelectChangeEvent<typeof documentType>) =>
                        setDocumentType(e.target.value)
                      }
                      name='documentType'
                      value={documentType}
                      tabIndex={-1}
                      //defaultOpen={true}
                      sx={{ width: '100%' }}
                    >
                      {' '}
                      <MenuItem id='aiSummaryDialogOtherMenuItem' value='other'>
                        {t('documentTypes.other')}
                      </MenuItem>
                      <MenuItem id='aiSummaryDialogAgreementMenuItem' value='agreement'>
                        {t('documentTypes.agreement')}
                      </MenuItem>
                      <MenuItem id='aiSummaryDialogContractMenuItem' value='contract'>
                        {t('documentTypes.contract')}
                      </MenuItem>
                      <MenuItem id='aiSummaryDialogInvoiceMenuItem' value='invoice'>
                        {t('tagsLabels.documentType.invoice')}
                      </MenuItem>
                      <MenuItem id='aiSummaryDialogInsuranceMenuItem' value='insurance'>
                        {t('tagsLabels.documentType.insurance')}
                      </MenuItem>
                      <MenuItem id='aiSummaryDialogFormMenuItem' value='form'>
                        {t('tagsLabels.documentType.form')}
                      </MenuItem>
                      <MenuItem
                        id='aiSummaryDialogCertificateMenuItem'
                        value='certificate or license'
                      >
                        {t('documentTypes.certificate')}
                      </MenuItem>
                      <MenuItem
                        id='aiSummaryDialogPoliciesAndProceduresMenuItem'
                        value='policies and procedures'
                      >
                        {t('tagsLabels.documentType.policiesAndProcedures')}
                      </MenuItem>
                      <MenuItem id='aiSummaryDialogLetterMenuItem' value='letter'>
                        {t('tagsLabels.documentType.letter')}
                      </MenuItem>
                      <MenuItem id='aiSummaryDialogSubscriptionMenuItem' value='subscription'>
                        {t('documentTypes.subscription')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid component='div' size={{ xs: 12, md: 12 }}>
                  <FormControl className={styles.w100}>
                    <Autocomplete
                      className={'autocomplete-container'}
                      multiple
                      id='tags-standard'
                      openOnFocus={true}
                      options={purposes}
                      disableCloseOnSelect={true}
                      isOptionEqualToValue={(option, value) => option.title === value.title}
                      getOptionLabel={(option) => option.title}
                      value={purpose}
                      onChange={(
                        event: React.SyntheticEvent,
                        newValue: AutocompleteValueType[]
                      ) => {
                        setPurpose(newValue);
                      }}
                      renderOption={(props, option, { selected }) => {
                        const { ...optionProps } = props;
                        return (
                          <li {...optionProps}>
                            <Checkbox
                              id={`aiSummaryDialogPurposeCheckbox_${String(option.title)
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, '-')}`}
                              icon={icon}
                              checkedIcon={checkedIcon}
                              checked={selected}
                            />
                            {option.title}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField {...params} variant='standard' label={t('labels.purpose')} />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid component='div' size={{ xs: 12, md: 6 }}>
                  <FormControl sx={{ width: '100%' }}>
                    <InputLabel htmlFor='resultFormatSelect'>{t('labels.resultFormat')}</InputLabel>
                    <Select
                      id='resultFormatSelect'
                      onChange={(
                        e:
                          | ChangeEvent<
                              Omit<HTMLInputElement, 'value'> & {
                                value: string;
                              }
                            >
                          | (Event & {
                              target: {
                                value: string;
                                name: string;
                              };
                            })
                      ) => {
                        setResultFormat(e.target.value);
                      }}
                      slotProps={{ input: { id: 'resultFormatSelect' } }}
                      name='resultFormat'
                      value={resultFormat}
                      tabIndex={-1}
                      sx={{ width: '100%' }}
                    >
                      <MenuItem id='aiSummaryDialogResultFormatEmptyMenuItem' value={''}>
                        {''}
                      </MenuItem>
                      <MenuItem id='aiSummaryDialogTableMenuItem' value='table'>
                        {t('resultFormat.table')}
                      </MenuItem>
                      <MenuItem id='aiSummaryDialogBulletPointMenuItem' value='bullet point'>
                        {t('resultFormat.bulletPoint')}
                      </MenuItem>
                      <MenuItem
                        id='aiSummaryDialogNarrativeFormatMenuItem'
                        value='narrative format'
                      >
                        {t('resultFormat.narrativeFormat')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid component='div' size={{ xs: 12, md: 6 }}>
                  <FormControl sx={{ width: '100%' }}>
                    <InputLabel htmlFor='detailLevelSelect'>{t('labels.detailLevel')}</InputLabel>
                    <Select
                      id='detailLevelSelect'
                      onChange={(
                        e:
                          | ChangeEvent<
                              Omit<HTMLInputElement, 'value'> & {
                                value: string;
                              }
                            >
                          | (Event & {
                              target: {
                                value: string;
                                name: string;
                              };
                            })
                      ) => {
                        setDetailLevel(e.target.value);
                      }}
                      slotProps={{ input: { id: 'detailLevelSelect' } }}
                      name='detailLevel'
                      value={detailLevel}
                      tabIndex={-1}
                      sx={{ width: '100%' }}
                    >
                      <MenuItem id='aiSummaryDialogHighLevelMenuItem' value='high level'>
                        {t('detailLevel.highLevel')}
                      </MenuItem>
                      <MenuItem id='aiSummaryDialogDetailedMenuItem' value='detailed'>
                        {t('detailLevel.detailed')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid
                  component='div'
                  size={{ xs: 12, md: 12 }}
                  className='checkbox-row'
                  style={{ display: 'flex', alignItems: 'flex-start' }}
                >
                  <Checkbox
                    style={{ marginLeft: -10, paddingTop: 0, paddingBottom: 0 }}
                    id='simplifyTechnicalOrLegalJargon'
                    checked={simplifyJargon}
                    onChange={(event) => setSimplifyJargon(event.target.checked)}
                  />
                  <label
                    id='aiSummaryDialogSimplifyJargonLabel'
                    style={{ marginTop: 0 }}
                    htmlFor='simplifyTechnicalOrLegalJargon'
                  >
                    {t('labels.simplifyJargon')}
                  </label>
                </Grid>
                <Grid component='div' size={{ xs: 12, md: 12 }}>
                  <FormControl className={styles.textarea_container}>
                    <textarea
                      id='contextualInformation'
                      name='contextualInformation'
                      value={contextualInformation}
                      placeholder={t('messages.contextualInformationPlaceholder')}
                      onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                        setContextualInformation(e.target.value);
                      }}
                    />
                    <InputLabel className={'label-transitioned'} htmlFor='contextualInformation'>
                      {t('labels.contextualInformation')}
                    </InputLabel>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid
                component='div'
                container
                style={{
                  justifyContent: 'flex-end',
                  gap: '8px',
                  flexWrap: 'wrap',
                  marginTop: '16px',
                }}
              >
                <Button
                  id='aiSummaryDialogGoBackBtn'
                  variant='contained'
                  onClick={onClose}
                  className='btn-default btn-action-basic btn-cancel'
                >
                  {t('buttons.goBack')}
                </Button>
                <Button
                  id='aiSummaryDialogAnalyzeBtn'
                  variant='contained'
                  onClick={() => {
                    if (handleAnalyzeFunction) {
                      handleAnalyzeFunction();
                    }
                  }}
                  className='btn-default btn-action-basic'
                >
                  {t('buttons.analyze')}
                </Button>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/*</Modal>*/}
    </>
  );
};

export default AISummaryDialog;
